import * as React from 'react';

import { FileCard } from '@rematter/modules/customer-portal/_components/_shared/FileCard';
import { FileRecord } from '@rematter/modules/customer-portal/types';
import { cn, Loading } from '@rematter/ui-web';
import { Button, DropZone } from '@rematter/ui-web';
import { ContentCard } from '@rematter/ui-web';
import { DropItem, FileDropItem, FileTrigger } from 'react-aria-components';

export type EntityTabAttachmentsProps = {
  files?: FileRecord[];
  uploadProgress: { isUploading: boolean; progress: number };
  onAddFiles: (files: File[]) => void;
  onDeleteFile: (file: { fileId: string }) => Promise<boolean>;
  className?: string;
  isLoading?: boolean;
};

export const EntityTabAttachments = (props: EntityTabAttachmentsProps) => {
  const { files, uploadProgress, onAddFiles, onDeleteFile } = props;

  const handleFileDeletion = React.useCallback(
    (file: { id: string }) => onDeleteFile({ fileId: file.id }),
    [onDeleteFile],
  );

  const handleSelectFiles = React.useCallback(
    (e: FileList | null) => {
      const files = Array.from(e ?? []);
      onAddFiles(files);
    },
    [onAddFiles],
  );

  const handleDrop = React.useCallback(
    async (e: { items: DropItem[] }) => {
      const fileItems = e.items.filter(
        (file) => file.kind === 'file',
      ) as FileDropItem[];

      const files = await Promise.all(
        fileItems.map((fileItem) => fileItem.getFile()),
      );

      onAddFiles(files);
    },
    [onAddFiles],
  );

  return (
    <div className={cn('@EntityTabAttachments', 'p-5', props.className)}>
      <Loading isLoading={props.isLoading}>
        <ContentCard className="flex flex-col gap-3">
          <div className="text-sm font-semibold">Attachments</div>
          <DropZone
            onDrop={handleDrop}
            className="flex h-[130px] w-full flex-col items-center justify-center rounded-md border-2 border-dashed border-primary/40 bg-primary/5"
          >
            {uploadProgress.isUploading && (
              <>
                <div className="text-sm text-primary/60">Uploading...</div>
                <div className="h-[5px] w-[200px] overflow-hidden rounded-full bg-primary/20">
                  <div
                    className="h-full rounded-full bg-primary"
                    style={{ width: `${uploadProgress.progress}%` }}
                  />
                </div>
              </>
            )}
            {!uploadProgress.isUploading && (
              <>
                <FileTrigger allowsMultiple onSelect={handleSelectFiles}>
                  <Button>Select files</Button>
                </FileTrigger>
                <div className="text-sm text-primary/60">
                  If you prefer, just drop files here
                </div>
              </>
            )}
          </DropZone>
          {!!files?.length && (
            <div className="mt-3 grid grid-cols-2 gap-3">
              {files?.map((file) => (
                <FileCard
                  key={file.id}
                  file={file}
                  onFileDelete={handleFileDeletion}
                />
              ))}
            </div>
          )}
        </ContentCard>
      </Loading>
    </div>
  );
};
